import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { heartPath } from "../gift/hearts";

const stormMilliseconds = 30000;
const countUpMilliseconds = 20000;
const countUpTarget = 67000;
const buryMilliseconds = 16000;
const dissolveMilliseconds = 1500;
const gentleFadeMilliseconds = 1200;
const gentleHoldMilliseconds = 3200;
const maxPixelRatio = 1.5;
const maxLiveKisses = 240;
const spriteSize = 128;
const gravity = 2400;
const pileTone = "#f5c6d1";
const heartTones = ["#e0245e", "#d81b50", "#ef3b6e"];

const createSpriteCanvas = () => {
  const canvas = document.createElement("canvas");
  canvas.width = spriteSize;
  canvas.height = spriteSize;
  return canvas;
};

const hasInk = (canvas) => {
  const pixels = canvas.getContext("2d").getImageData(0, 0, spriteSize, spriteSize).data;
  for (let index = 3; index < pixels.length; index += 16) {
    if (pixels[index] > 0) return true;
  }
  return false;
};

const drawEmojiSprite = () => {
  const canvas = createSpriteCanvas();
  const context = canvas.getContext("2d");
  context.font = `${spriteSize * 0.74}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillText("\u{1F48B}", spriteSize / 2, spriteSize * 0.56);
  return hasInk(canvas) ? canvas : null;
};

const drawHeartSprite = (tone) => {
  const canvas = createSpriteCanvas();
  const context = canvas.getContext("2d");
  context.translate(spriteSize * 0.08, spriteSize * 0.08);
  context.scale((spriteSize * 0.84) / 24, (spriteSize * 0.84) / 24);
  context.fillStyle = tone;
  context.fill(new Path2D(heartPath));
  return canvas;
};

const buildSprites = () => {
  const emoji = drawEmojiSprite();
  const hearts = heartTones.map(drawHeartSprite);
  return { kisses: emoji ? [emoji] : hearts, hearts };
};

const pickFrom = (list) => list[Math.floor(Math.random() * list.length)];

const prepareCanvas = (canvas, width, height, pixelRatio) => {
  canvas.width = Math.round(width * pixelRatio);
  canvas.height = Math.round(height * pixelRatio);
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  const context = canvas.getContext("2d");
  context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
  return context;
};

const stamp = (context, sprite, x, y, size, rotation, alpha = 1) => {
  context.save();
  context.globalAlpha = alpha;
  context.translate(x, y);
  context.rotate(rotation);
  context.drawImage(sprite, -size / 2, -size / 2, size, size);
  context.restore();
};

const fillUnderPile = (context, width, height, lineY, elapsedSeconds) => {
  if (lineY >= height) return;
  context.save();
  context.globalCompositeOperation = "destination-over";
  context.fillStyle = pileTone;
  context.beginPath();
  context.moveTo(0, height);
  for (let x = 0; x <= width + 24; x += 24) {
    context.lineTo(x, lineY + Math.sin(x * 0.03 + elapsedSeconds * 2) * 14);
  }
  context.lineTo(width, height);
  context.closePath();
  context.fill();
  context.restore();
};

const formatCount = (value) => `kisses: ${Math.round(value).toLocaleString("en-US")}`;
const infiniteCount = "kisses: \u221E";

export default function KissStorm({ onDone }) {
  const shellRef = useRef(null);
  const pileRef = useRef(null);
  const liveRef = useRef(null);
  const counterRef = useRef(null);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    const shell = shellRef.current;
    const width = window.innerWidth;
    const height = window.innerHeight;
    const pixelRatio = Math.min(window.devicePixelRatio || 1, maxPixelRatio);
    const pile = prepareCanvas(pileRef.current, width, height, pixelRatio);
    const live = prepareCanvas(liveRef.current, width, height, pixelRatio);
    const sprites = buildSprites();
    const areaScale = Math.sqrt(Math.min(4, (width * height) / (390 * 844)));
    const prefersGentle = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const timers = [];
    let frame = 0;

    const finish = () => {
      shell.style.transition = `opacity ${dissolveMilliseconds}ms ease, transform ${dissolveMilliseconds}ms ease`;
      shell.style.opacity = "0";
      shell.style.transform = "scale(1.06)";
      shell.style.pointerEvents = "none";
      timers.push(setTimeout(() => onDoneRef.current(), dissolveMilliseconds + 60));
    };

    if (prefersGentle) {
      pile.fillStyle = pileTone;
      pile.fillRect(0, 0, width, height);
      const total = Math.ceil(((width * height) / 2600) * 1.4);
      for (let index = 0; index < total; index += 1) {
        const sprite = Math.random() < 0.8 ? sprites.kisses[0] : pickFrom(sprites.hearts);
        stamp(pile, sprite, Math.random() * width, Math.random() * height, (34 + Math.random() * 60) * areaScale, (Math.random() - 0.5) * 1.2);
      }
      counterRef.current.textContent = formatCount(total * 7);
      shell.style.opacity = "0";
      shell.style.transition = `opacity ${gentleFadeMilliseconds}ms ease`;
      frame = requestAnimationFrame(() => {
        shell.style.opacity = "1";
      });
      timers.push(setTimeout(finish, gentleFadeMilliseconds + gentleHoldMilliseconds));
      return () => {
        cancelAnimationFrame(frame);
        timers.forEach(clearTimeout);
      };
    }

    const liveKisses = [];
    const startedAt = performance.now();
    let lastTime = startedAt;
    let spawnDebt = 0;
    let shownCount = 0;
    let lastCounterUpdate = 0;
    let isStopped = false;
    let spawnCapPerFrame = 30;

    const spawn = (elapsedSeconds, lineY) => {
      const isHeart = Math.random() < 0.25;
      const sprite = isHeart ? pickFrom(sprites.hearts) : sprites.kisses[0];
      const growth = 1 + Math.min(1.2, elapsedSeconds / 10);
      const size = (isHeart ? 12 + Math.random() * 18 : 28 + Math.random() * 50) * growth * areaScale;
      const x = Math.random() * width;
      const isSplat = Math.random() < 0.38;
      const restingY = isSplat
        ? Math.random() * height
        : Math.min(height + size * 0.2, lineY - size * 0.35 + Math.random() * Math.max(size, (height - lineY) * 0.3));
      const rotation = (Math.random() - 0.5) * 1.3;
      shownCount += 1;
      if (liveKisses.length >= maxLiveKisses) {
        stamp(pile, sprite, x, restingY, size, rotation);
        return;
      }
      liveKisses.push({
        sprite,
        x,
        y: isSplat ? restingY : -size,
        restingY,
        size,
        rotation,
        spin: (Math.random() - 0.5) * 5,
        velocity: 260 + Math.random() * 520,
        age: 0,
        isSplat,
      });
    };

    const tick = (now) => {
      const deltaSeconds = Math.min(0.05, (now - lastTime) / 1000);
      lastTime = now;
      const elapsed = now - startedAt;
      const elapsedSeconds = elapsed / 1000;
      const buryProgress = Math.min(1, elapsed / buryMilliseconds);
      const lineY = height * (1.05 - 1.25 * Math.pow(buryProgress, 1.7));

      if (!isStopped) {
        spawnCapPerFrame = deltaSeconds < 0.02 ? Math.min(3000, spawnCapPerFrame * 1.1 + 2) : Math.max(8, spawnCapPerFrame * 0.75);
        const rate = 8 * Math.pow(1.7, elapsedSeconds);
        spawnDebt = Math.min(spawnDebt + rate * deltaSeconds, spawnCapPerFrame);
        while (spawnDebt >= 1) {
          spawn(elapsedSeconds, lineY);
          spawnDebt -= 1;
        }
        fillUnderPile(pile, width, height, lineY + 46, elapsedSeconds);
      }

      live.clearRect(0, 0, width, height);
      for (let index = liveKisses.length - 1; index >= 0; index -= 1) {
        const kiss = liveKisses[index];
        kiss.age += deltaSeconds;
        if (kiss.isSplat) {
          const progress = Math.min(1, kiss.age / 0.24);
          const scale = 1.9 - 0.9 * progress;
          if (progress >= 1) {
            stamp(pile, kiss.sprite, kiss.x, kiss.y, kiss.size, kiss.rotation);
            liveKisses.splice(index, 1);
          } else {
            stamp(live, kiss.sprite, kiss.x, kiss.y, kiss.size * scale, kiss.rotation, progress);
          }
          continue;
        }
        kiss.velocity += gravity * deltaSeconds;
        kiss.y += kiss.velocity * deltaSeconds;
        kiss.rotation += kiss.spin * deltaSeconds;
        if (kiss.y >= kiss.restingY) {
          stamp(pile, kiss.sprite, kiss.x, kiss.restingY, kiss.size, kiss.rotation);
          liveKisses.splice(index, 1);
        } else {
          stamp(live, kiss.sprite, kiss.x, kiss.y, kiss.size, kiss.rotation);
        }
      }

      if (now - lastCounterUpdate > 90 && counterRef.current) {
        lastCounterUpdate = now;
        const countUpProgress = elapsed / countUpMilliseconds;
        counterRef.current.textContent =
          countUpProgress >= 1
            ? infiniteCount
            : formatCount(Math.min(countUpTarget, Math.max(shownCount, countUpTarget * Math.pow(Math.min(1, countUpProgress / 0.96), 2.4))));
      }

      if (!isStopped) {
        const shake = elapsedSeconds < 3 ? 0 : Math.min(3.5, (elapsedSeconds - 3) * 0.3);
        const shakeX = (Math.random() - 0.5) * shake * 2;
        const shakeY = (Math.random() - 0.5) * shake * 2;
        shell.style.transform = `translate3d(${shakeX.toFixed(1)}px, ${shakeY.toFixed(1)}px, 0) scale(1.02)`;
      }

      if (!isStopped && elapsed >= stormMilliseconds) {
        isStopped = true;
        finish();
      }
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      timers.forEach(clearTimeout);
    };
  }, []);

  return createPortal(
    <div
      ref={shellRef}
      className="kiss-storm"
      role="presentation"
      onPointerDown={(event) => event.preventDefault()}
      onWheel={(event) => event.stopPropagation()}
    >
      <canvas ref={pileRef} className="kiss-storm-layer" />
      <canvas ref={liveRef} className="kiss-storm-layer" />
      <div ref={counterRef} className="kiss-storm-counter" aria-hidden="true">
        {formatCount(0)}
      </div>
    </div>,
    document.body
  );
}
